import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, concatMap, map, of } from 'rxjs';
import { DetailsListComponent } from 'src/shared/components/design/detailslist/detailslist.component';
import { DetailsListConfig, DetailsListItemType } from 'src/shared/components/design/detailslist/detailslist.interface';
import { ErrorComponent } from 'src/shared/components/design/error/error.component';
import { LoaderComponent } from 'src/shared/components/design/loader-copy/loader.component';
import { TitleComponent } from 'src/shared/components/design/title/title.component';
import { bookinginternal } from 'src/shared/services/client/client';
import { IsClientErrorMessagePipe } from 'src/shared/services/client/client.error.pipe';
import { ConsultationService } from 'src/shared/services/consultations/consultations.service';
import { AppointmentsDataService } from '../../services/appointments.data.service/appointments.data.service';

@Component({
  selector: 'app-appointmentdetails',
  templateUrl: './appointmentdetails.component.html',
  imports: [TitleComponent, ErrorComponent, AsyncPipe, IsClientErrorMessagePipe, DetailsListComponent, LoaderComponent],
})
export class AppointmentDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private appointmentsDataService = inject(AppointmentsDataService);
  private consultationService = inject(ConsultationService);
  private toastrService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  get id() {
    return this.route.snapshot.paramMap.get('id');
  }

  ngOnInit(): void {
    this.load();
  }

  appointment$!: Observable<bookinginternal.GetAppointmentResponse>;

  load() {
    this.appointment$ = this.appointmentsDataService
      .GetAppointment(this.id ?? '', {
        // TODO - set this from cetnral service
        Timezone: 'Europe/London',
      })
      .pipe(
        catchError((err) => {
          return of(err);
        })
      );
  }

  back() {
    this.router.navigate([`/appointments`]);
  }

  start(petId: string, ownerId: string) {
    this.consultationService
      .CreateConsultation({
        appointment_id: this.id!,
        pet_id: petId,
        owner_id: ownerId,
        notes: '',
      })
      .pipe(
        concatMap((consultation) =>
          this.appointmentsDataService
            .UpdateAppointmentConsultStatus(this.id ?? '', {
              consult_status: 'InReview',
              disrupted_reason: '',
            })
            .pipe(map(() => consultation.id))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (consultationId: string) => {
          this.router.navigate([`/consultation/${consultationId}`]);
        },
        error: (error) => {
          const fallbackConsultationId = error?.details?.consultation_id;

          if (fallbackConsultationId) {
            this.router.navigate([`/consultation/${fallbackConsultationId}`]);
          } else {
            this.toastrService.error(error.message ? error.message : 'Problem creating consultation');
          }
        },
      });
  }

  continue(appointmentId: string) {
    this.consultationService
      .GetConsultationIdByAppointment(appointmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp) => {
        this.router.navigate([`/consultation/${resp.consultation_doc_id}`]);
      });
  }

  getConfig(data: bookinginternal.GetAppointmentResponse): DetailsListConfig {
    let config: DetailsListConfig = {
      items: [
        {
          id: 'start_time',
          title: 'Start Time',
          content: data.start.time_friendly,
          type: DetailsListItemType.Text,
        },
        {
          id: 'end_time',
          title: 'End Time',
          content: data.end.time_friendly,
          type: DetailsListItemType.Text,
        },
        {
          id: 'status',
          title: 'Status',
          content: data.status,
          type: DetailsListItemType.Text,
        },
      ],
    };
    return config;
  }
}
